import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {DEBOUNCE_MS, NOTIFICATION_TIMEOUTS} from 'src/constants';
import {useApiWithNotifications} from 'src/hooks/api-with-notification/useApiWithNotifications';
import {useDebounce} from 'src/hooks/use-debounce';
import {createApprovalRule} from 'src/openapi/admin/apis/ApprovalRuleApi';
import {getAvailableGrants} from 'src/openapi/admin/apis/RuleGrantApi';
import {GrantStatus} from 'src/openapi/admin/models/GrantStatus';
import {VendorApprovalRule, VendorApprovalRuleEntitlementStatus} from 'src/openapi/admin/models/VendorApprovalRule';
import {useAppDispatch} from 'src/store';


import type {Grant} from 'src/openapi/admin/models/Grant';

type Params = {
    vendorId?: string,
    existingRules: VendorApprovalRule[],
    initialGrants: Grant[],
    onCreated: (rule: VendorApprovalRule) => void,
    onClose: () => void,
};

const DUPLICATE_RULE_MESSAGE = 'A rule with this name already exists.';
const GENERIC_ERROR_MESSAGE = 'Unable to create rule. Please try again.';
const MIN_GRANT_SEARCH_LEN = 3;

const mapGrantStatusToEntitlementStatus = (status: GrantStatus): VendorApprovalRule['entitlementStatus'] => {
    if (status === GrantStatus.ALLOW) {
        return VendorApprovalRuleEntitlementStatus.ALLOW;
    }
    return VendorApprovalRuleEntitlementStatus.DENY;
};

export const useAddRuleDialog = ({
    vendorId,
    existingRules,
    initialGrants,
    onCreated,
    onClose,
}: Params) => {
    const dispatch = useAppDispatch();
    const {executeWithNotifications} = useApiWithNotifications();
    const [ruleName, setRuleName] = useState('');
    const [selectedGrant, setSelectedGrant] = useState<Grant | null>(null);
    const [grantQuery, setGrantQuery] = useState('');
    const [grantResults, setGrantResults] = useState<Grant[]>(initialGrants);
    const [grantSuggestShown, setGrantSuggestShown] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [isMinQueryShown, setIsMinQueryShown] = useState(false);
    const [searchEnabled, setSearchEnabled] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const searchController = useRef<AbortController | null>(null);
    const latestSearchId = useRef(0);

    useEffect(() => {
        setGrantResults(initialGrants);
    }, [initialGrants]);

    useEffect(() => () => {
        if (searchController.current) {
            searchController.current.abort();
        }
    }, []);

    const debouncedSearch = useDebounce((term: string) => {
        setHasSearched(true);
        setSearchLoading(true);
        setSearchError(null);
        latestSearchId.current += 1;
        const currentId = latestSearchId.current;

        if (searchController.current) {
            searchController.current.abort();
        }

        const controller = new AbortController();
        searchController.current = controller;

        dispatch(getAvailableGrants(term ? {grantName: term} : {}, {signal: controller.signal}))
            .then(response => {
                if (controller.signal.aborted || currentId !== latestSearchId.current) {
                    return;
                }
                if (response.payload?.success) {
                    setGrantResults((response.payload.data as Grant[]) ?? []);
                } else {
                    setGrantResults([]);
                    setSearchError('Unable to load grants. Please try again.');
                }
            })
            .catch(err => {
                if ((err as {name?: string})?.name === 'AbortError' || controller.signal.aborted) {
                    return;
                }
                setGrantResults([]);
                setSearchError('Unable to load grants. Please try again.');
            })
            .finally(() => {
                if (!controller.signal.aborted && currentId === latestSearchId.current) {
                    setSearchLoading(false);
                }
            });
    }, DEBOUNCE_MS);

    const handleGrantQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;

        if (!searchEnabled) {
            setSearchEnabled(true);
        }

        setGrantQuery(value);
        setGrantSuggestShown(true);
        setSearchError(null);
        if (error) {
            setError(null);
        }
        setIsMinQueryShown(false);

        if (selectedGrant && selectedGrant.grantName !== value && selectedGrant.grantId !== value) {
            setSelectedGrant(null);
        }

        if (!value.trim()) {
            if (searchController.current) {
                searchController.current.abort();
            }
            setHasSearched(false);
            setGrantResults(initialGrants);
            setSearchLoading(false);
            setIsMinQueryShown(false);
            return;
        }

        const trimmed = value.trim();
        if (trimmed.length < MIN_GRANT_SEARCH_LEN) {
            if (searchController.current) {
                searchController.current.abort();
            }
            setHasSearched(false);
            setGrantResults(initialGrants);
            setSearchLoading(false);
            setIsMinQueryShown(true);
            return;
        }

        setIsMinQueryShown(false);
        debouncedSearch(trimmed);
    }, [debouncedSearch, error, initialGrants, searchEnabled, selectedGrant]);

    const handleGrantFocus = useCallback(() => {
        if (!searchEnabled) {
            return;
        }
        setGrantSuggestShown(true);
        if (error) {
            setError(null);
        }
        if (grantQuery.trim().length > 0 && grantQuery.trim().length < MIN_GRANT_SEARCH_LEN) {
            setIsMinQueryShown(true);
        }
    }, [error, grantQuery, searchEnabled]);

    const handleGrantSelect = useCallback((grant: Grant) => {
        setSelectedGrant(grant);
        setGrantQuery(grant.grantName);
        setGrantSuggestShown(false);
        setSearchEnabled(false);
        setHasSearched(true);
        setIsMinQueryShown(false);
        if (error) {
            setError(null);
        }
    }, [error]);

    const clearGrantSelection = useCallback(() => {
        if (searchController.current) {
            searchController.current.abort();
        }
        setGrantQuery('');
        setSelectedGrant(null);
        setGrantResults(initialGrants);
        setGrantSuggestShown(false);
        setHasSearched(false);
        setSearchLoading(false);
        setSearchError(null);
        setIsMinQueryShown(false);
        setSearchEnabled(true);
    }, [initialGrants]);

    const handleRuleNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setRuleName(event.currentTarget.value);
        if (error) {
            setError(null);
        }
    }, [error]);

    const handleClose = useCallback(() => {
        setRuleName('');
        clearGrantSelection();
        setError(null);
        setIsMinQueryShown(false);
        onClose();
    }, [clearGrantSelection, onClose]);

    const hasDuplicateName = useMemo(() => {
        const normalized = ruleName.trim().toLowerCase();
        if (!normalized) {
            return false;
        }
        return existingRules.some(rule => rule.ruleName.trim().toLowerCase() === normalized);
    }, [existingRules, ruleName]);

    useEffect(() => {
        if (hasDuplicateName) {
            setError(DUPLICATE_RULE_MESSAGE);
        } else if (error === DUPLICATE_RULE_MESSAGE) {
            setError(null);
        }
    }, [error, hasDuplicateName]);

    const isCreateDisabled = busy || !vendorId || !ruleName.trim() || !selectedGrant || hasDuplicateName;

    const handleCreateRule = useCallback(async () => {
        const trimmedName = ruleName.trim();
        if (!trimmedName) {
            setError('Rule name is required.');
            return;
        }
        if (!selectedGrant) {
            setError('Please select a grant.');
            return;
        }
        if (!vendorId) {
            setError(GENERIC_ERROR_MESSAGE);
            return;
        }
        if (hasDuplicateName) {
            setError(DUPLICATE_RULE_MESSAGE);
            return;
        }

        const grant = selectedGrant;

        setBusy(true);
        setError(null);

        try {
            const response = await executeWithNotifications<VendorApprovalRule>(
                createApprovalRule({
                    createRuleRequest: {
                        ruleName: trimmedName,
                        vendorId,
                        entitlementId: grant.grantId,
                    },
                }),
                {
                    success: 'Rule successfully created.',
                    error: GENERIC_ERROR_MESSAGE,
                    exception: GENERIC_ERROR_MESSAGE,
                },
                'create-approval-rule',
                NOTIFICATION_TIMEOUTS,
            );

            if (response.success && response.data) {
                const createdRule: VendorApprovalRule = {
                    ...response.data,
                    entitlementName: response.data.entitlementName ?? grant.grantName,
                    entitlementDescription: response.data.entitlementDescription ?? grant.grantDescription,
                    entitlementStatus: response.data.entitlementStatus
                        ?? mapGrantStatusToEntitlementStatus(grant.status),
                };
                onCreated(createdRule);
                handleClose();
            } else {
                setError(GENERIC_ERROR_MESSAGE);
            }
        } catch {
            setError(GENERIC_ERROR_MESSAGE);
        } finally {
            setBusy(false);
        }
    }, [executeWithNotifications, handleClose, hasDuplicateName, onCreated, ruleName, selectedGrant, vendorId]);

    const displayError = error ?? searchError;
    const shouldShowNoGrants = grantSuggestShown
        && !searchLoading
        && grantResults.length === 0
        && !hasSearched
        && !isMinQueryShown;

    return {
        ruleName,
        grantQuery,
        grantResults,
        grantSuggestShown,
        searchLoading,
        hasSearched,
        isMinQueryShown,
        shouldShowNoGrants,
        isCreateDisabled,
        busy,
        displayError,
        handleGrantQueryChange,
        handleGrantSelect,
        handleGrantFocus,
        setGrantSuggestShown,
        handleRuleNameChange,
        handleCreateRule,
        handleClose,
        clearGrantSelection,
    };
};
